import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { calcPitia, rentNeededForDscr } from "@/lib/investor-calcs";
import rentNeededHero from "@/assets/hero/hero-rent-needed-dscr-tablet-exact-v3.jpg";

const FAQS = [
  {
    q: "How much rent do I need for a DSCR loan?",
    a: "Multiply the full monthly PITIA — principal and interest, property taxes, insurance, flood if applicable, and HOA dues — by the program's minimum DSCR. At a 1.00 minimum you need rent equal to PITIA; at 1.25 you need 25% more than PITIA.",
  },
  {
    q: "Which rent figure does a DSCR lender use?",
    a: "Generally the lower of the in-place lease rent and the appraiser's market rent opinion (Form 1007 for single-family, Form 1025 for 2–4 units). Some programs use market rent on a vacant unit. Short-term rental programs may use documented platform revenue instead.",
  },
  {
    q: "Does the lender subtract vacancy or management from rent?",
    a: "Most long-term DSCR programs qualify on gross rent and do not deduct vacancy or management. Some short-term rental and 5+ unit programs underwrite net of expenses. The vacancy/management field here lets you model that stricter treatment.",
  },
  {
    q: "What if my rent falls short of the target DSCR?",
    a: "Common levers are a larger down payment, an interest-only payment structure, buying the rate down with points, a longer amortization, or shopping a program with a lower DSCR floor. A no-ratio DSCR structure removes the ratio test at lower leverage.",
  },
  {
    q: "Is DSCR calculated before or after the mortgage payment?",
    a: "DSCR is rent divided by the housing payment including the mortgage, so the payment is the denominator. Cash flow is what remains after that payment plus operating costs.",
  },
  {
    q: "How is required rent affected by choosing interest-only?",
    a: "Because interest-only removes the principal portion of PITIA, the required rent to hit a given DSCR target drops compared with a fully amortizing structure at the same loan amount and rate. It is one of the more direct levers for closing a rent shortfall.",
  },
  {
    q: "What DSCR do most investor programs require?",
    a: "Common minimums range from 1.00 to 1.25, with some programs allowing DSCR as low as 0.75-1.00 at reduced leverage, and a few no-ratio options skipping the test entirely at lower LTV. The exact floor depends on the lender, property type, and loan amount.",
  },
  {
    q: "Does a higher down payment lower the rent I need?",
    a: "Yes, a larger down payment reduces the loan amount and therefore the principal and interest portion of PITIA, which lowers the rent needed to reach the same DSCR target. It is often the most direct way to bridge a rent shortfall on a purchase.",
  },
  {
    q: "How do appraisers determine market rent for DSCR qualifying?",
    a: "Appraisers complete a comparable rent schedule — Form 1007 for single-family and Form 1025 for 2-4 units — that surveys similar nearby rentals to estimate fair market rent for the subject property. Lenders then typically use the lower of that appraised rent and any existing signed lease.",
  },
  {
    q: "Can projected rent increases help me qualify today?",
    a: "Generally no — DSCR qualifying uses current lease rent or the appraiser's current market rent opinion, not a future projected increase, so planned rent bumps do not count until they are actually in place. Underwrite to today's documented rent, not tomorrow's hoped-for rent.",
  },
  {
    q: "Does a short-term rental need more rent to qualify than a long-term lease?",
    a: "Short-term rental DSCR programs often qualify off documented platform revenue history and may net out operating expenses like cleaning and management, which can require a higher gross revenue figure to clear the same DSCR target than a long-term lease would. Requirements vary widely by program.",
  },
  {
    q: "What if the appraised rent comes in lower than expected?",
    a: "A lower appraised rent shrinks the numerator in the DSCR formula, which can push the loan below the program's minimum ratio even if the purchase price and loan terms have not changed. Common fixes include a larger down payment, an interest-only structure, or points to buy down the payment.",
  },
];

export const Route = createFileRoute("/rent-needed-for-dscr")({
  head: () =>
    buildHead({
      title: "Rent Needed for DSCR Calculator | Simply Approved",
      description:
        "Find the exact monthly rent an investment property needs to hit a 1.0, 1.10 or 1.25 DSCR. Enter loan, rate, taxes, insurance and HOA to see the required rent and your gap.",
      url: "/rent-needed-for-dscr",
      keywords:
        "rent needed for DSCR, DSCR rent requirement calculator, how much rent for a DSCR loan, minimum rent DSCR 1.25, DSCR qualifying rent, market rent DSCR loan, rent to PITIA ratio, DSCR shortfall calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Rent Needed for DSCR", url: "/rent-needed-for-dscr" },
      ],
      service: { name: "Rent Needed for DSCR Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Rent Needed for DSCR Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/rent-needed-for-dscr",
          description:
            "Calculate the monthly rent required to reach a target DSCR on an investment property loan.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: RentNeededPage,
});

type Inputs = {
  loanAmount: number;
  ratePct: number;
  termYears: number;
  ioToggle: number;
  taxesAnnual: number;
  insAnnual: number;
  floodAnnual: number;
  hoaMonthly: number;
  targetDscr: number;
  currentRent: number;
  haircutPct: number;
};

const DEFAULT: Inputs = {
  loanAmount: 300000,
  ratePct: 7.5,
  termYears: 30,
  ioToggle: 0,
  taxesAnnual: 4200,
  insAnnual: 1900,
  floodAnnual: 0,
  hoaMonthly: 0,
  targetDscr: 1.25,
  currentRent: 2900,
  haircutPct: 0,
};

function RentNeededPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("rent_needed", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("rent_needed", String(k), v as never);
  };

  const m = useMemo(() => {
    const p = calcPitia({
      loanAmount: i.loanAmount,
      ratePct: i.ratePct,
      termYears: i.termYears,
      interestOnly: i.ioToggle >= 1,
      taxesAnnual: i.taxesAnnual,
      insuranceAnnual: i.insAnnual,
      floodAnnual: i.floodAnnual,
      hoaMonthly: i.hoaMonthly,
    });
    const r = rentNeededForDscr(p.pitia, i.targetDscr, i.currentRent, i.haircutPct);
    return { p, r };
  }, [i]);

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("rent_needed", {
      required_rent: m.r.requiredGrossRent,
      target_dscr: i.targetDscr,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meets = (m.r.gapMonthly ?? 0) <= 0;

  const aiFacts = [
    { label: "Loan amount", value: usd(i.loanAmount) },
    { label: "Monthly PITIA", value: usd(m.p.pitia) },
    { label: "Target DSCR", value: i.targetDscr.toFixed(2) },
    { label: "Rent required", value: usd(m.r.requiredGrossRent) },
    { label: "Current / expected rent", value: usd(i.currentRent) },
    { label: "DSCR at current rent", value: (m.r.currentDscr ?? 0).toFixed(2) },
    { label: "Monthly gap", value: usd(Math.abs(m.r.gapMonthly ?? 0)) },
  ];

  return (
    <CalculatorScaffold
      calculator="rent_needed"
      eyebrow="Rent needed for DSCR"
      title="Rent Needed for a DSCR Loan"
      subtitle="Enter the loan and carrying costs to see the monthly rent required to reach your target DSCR — and how far the current rent is from it. Illustrative estimates only, not an approval or offer of credit."
      shareUrl={shareUrl}
      shareTitle="Rent needed to hit my target DSCR"
      heroImage={{
        src: rentNeededHero,
        alt: "Two investor friends at a kitchen table using a tablet to review the rent needed for a DSCR loan",
      }}
      inputs={
        <>
          <CalcCard title="1. Loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Loan amount" value={i.loanAmount} onChange={(v) => set("loanAmount", v)} prefix="$" />
              <CalcField label="Note rate" value={i.ratePct} onChange={(v) => set("ratePct", v)} suffix="%" step={0.125} hint="Use a scenario rate — this tool does not quote pricing." />
              <CalcField label="Amortization (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={5} />
              <CalcField label="Interest-only? (1 = yes)" value={i.ioToggle} onChange={(v) => set("ioToggle", v)} step={1} hint="Enter 1 to model an interest-only payment." />
            </div>
          </CalcCard>
          <CalcCard title="2. Taxes, insurance & dues">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" hint="Use the reassessed amount after sale, not the seller's bill." />
              <CalcField label="Hazard insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
              <CalcField label="Flood insurance (annual)" value={i.floodAnnual} onChange={(v) => set("floodAnnual", v)} prefix="$" />
              <CalcField label="HOA dues (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
            </div>
          </CalcCard>
          <CalcCard title="3. Target & current rent">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Target DSCR" value={i.targetDscr} onChange={(v) => set("targetDscr", v)} step={0.05} hint="Common program floors: 1.00, 1.10, 1.25." />
              <CalcField label="Current / expected rent" value={i.currentRent} onChange={(v) => set("currentRent", v)} prefix="$" />
              <CalcField label="Vacancy / management haircut" value={i.haircutPct} onChange={(v) => set("haircutPct", v)} suffix="%" step={1} hint="Leave at 0 for programs that qualify on gross rent." />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label="Rent required" value={usd(m.r.requiredGrossRent)} />
            <CalcStat label="Monthly PITIA" value={usd(m.p.pitia)} />
            <CalcStat
              label="DSCR at current rent"
              value={(m.r.currentDscr ?? 0).toFixed(2)}
              colorClass={meets ? "text-emerald-600" : "text-destructive"}
            />
            <CalcStat
              label={meets ? "Cushion" : "Shortfall"}
              value={usd(Math.abs(m.r.gapMonthly ?? 0))}
              colorClass={meets ? "text-emerald-600" : "text-destructive"}
            />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Payment build-up</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              <CalcRow label={i.ioToggle >= 1 ? "Interest-only payment" : "Principal & interest"} value={usd(m.p.pi)} />
              <CalcRow label="Property taxes" value={usd(m.p.taxes)} />
              <CalcRow label="Hazard insurance" value={usd(m.p.insurance)} />
              <CalcRow label="Flood insurance" value={usd(m.p.flood)} />
              <CalcRow label="HOA dues" value={usd(m.p.hoa)} />
              <CalcRow label="Total PITIA" value={usd(m.p.pitia)} strong />
              <CalcRow label={`Rent at ${i.targetDscr.toFixed(2)} DSCR`} value={usd(m.r.requiredMonthlyRent)} strong />
              {i.haircutPct > 0 && (
                <CalcRow label={`Gross rent before ${i.haircutPct}% haircut`} value={usd(m.r.requiredGrossRent)} strong />
              )}
              <CalcRow label="Required annual rent" value={usd(m.r.requiredAnnualRent)} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "Why does my DSCR fall short at this rent?",
        "How would interest-only change the required rent?",
        "What raises DSCR fastest — rate, leverage, or term?",
        "Which rent figure does an appraiser report?",
      ]}
      methodologyIntro="Required rent is a direct function of the payment, so the whole exercise is getting the payment right."
      methodology={
        <>
          <p>
            <strong>The formula.</strong> DSCR = qualifying rent ÷ PITIA. Rearranged, qualifying rent = PITIA ×
            target DSCR. At a $2,320 PITIA and a 1.25 target you need $2,900 of qualifying rent.
          </p>
          <p>
            <strong>Where investors get it wrong.</strong> Using the seller&apos;s current tax bill instead of the
            post-sale reassessed amount, quoting insurance from a soft-market renewal, and omitting HOA dues. Those
            three omissions routinely move DSCR by 0.10 or more.
          </p>
          <p>
            <strong>Interest-only.</strong> An interest-only payment lowers the denominator, so the same rent
            supports a higher ratio. It does not reduce the balance, so plan the exit before you use it as the fix.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Rent and DSCR FAQ"
      related={[
        { to: "/dscr-calculator", label: "DSCR calculator" },
        { to: "/dscr-payment-calculator", label: "DSCR payment (PITIA) calculator" },
        { to: "/interest-only-vs-amortizing-calculator", label: "Interest-only vs amortizing" },
        { to: "/dscr-loan-requirements", label: "DSCR loan requirements" },
        { to: "/no-ratio-dscr-loans", label: "No-ratio DSCR loans" },
        { to: "/cap-rate-calculator", label: "Cap rate calculator" },
      ]}
      ctaTitle="Need the rent gap closed?"
      ctaBody="Send the scenario and a licensed loan officer will walk through leverage, structure, and program options for business-purpose investment property financing."
    />
  );
}
